import { useParallax } from "@/hooks/use-parallax";
import { useState, useEffect } from "react";

export const ParallaxHeroBg = () => {
  const [isReady, setIsReady] = useState(false);
  const layer1 = useParallax({ speed: -0.15 });
  const layer2 = useParallax({ speed: -0.25 });
  const layer3 = useParallax({ speed: -0.35 });

  // Delay visibility until after first paint to prevent layout shift
  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      setIsReady(true);
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  return (
    <div 
      className="absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-300" 
      style={{ 
        contain: 'layout paint style',
        opacity: isReady ? 1 : 0,
      }}
    >
      {/* Gradient base - always visible, no transform */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/5" />
      
      {/* Floating shapes - Layer 1 (slowest) - transform: none initially to prevent CLS */}
      <div 
        ref={layer1.ref} 
        style={{ 
          position: 'absolute', 
          inset: 0, 
          willChange: isReady ? 'transform' : 'auto',
          transform: isReady ? layer1.style.transform : 'none',
          transition: layer1.style.transition,
        }} 
        className="absolute inset-0"
      >
        <div className="absolute top-[10%] left-[5%] w-72 h-72 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl" style={{ contain: 'layout' }} />
        <div className="absolute bottom-[20%] right-[10%] w-96 h-96 bg-gradient-to-tl from-secondary/10 to-transparent rounded-full blur-3xl" style={{ contain: 'layout' }} />
      </div>

      {/* Floating shapes - Layer 2 (medium) - transform: none initially to prevent CLS */}
      <div 
        ref={layer2.ref} 
        style={{ 
          position: 'absolute', 
          inset: 0, 
          willChange: isReady ? 'transform' : 'auto',
          transform: isReady ? layer2.style.transform : 'none',
          transition: layer2.style.transition,
        }} 
        className="absolute inset-0"
      >
        <div className="absolute top-[30%] right-[20%] w-48 h-48 bg-gradient-to-br from-primary/8 to-secondary/5 rounded-full blur-2xl" style={{ contain: 'layout' }} />
        <div className="absolute bottom-[40%] left-[15%] w-64 h-64 bg-gradient-to-tr from-secondary/8 to-primary/5 rounded-full blur-2xl" style={{ contain: 'layout' }} />
      </div>

      {/* Geometric accents - Layer 3 (fastest) - transform: none initially to prevent CLS */}
      <div 
        ref={layer3.ref} 
        style={{ 
          position: 'absolute', 
          inset: 0, 
          willChange: isReady ? 'transform' : 'auto',
          transform: isReady ? layer3.style.transform : 'none',
          transition: layer3.style.transition,
        }} 
        className="absolute inset-0"
      >
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--secondary)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--secondary)) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
        
        {/* Floating geometric shapes */}
        <div className="absolute top-[15%] right-[30%] w-4 h-4 border-2 border-primary/20 rotate-45" />
        <div className="absolute top-[60%] left-[10%] w-6 h-6 border-2 border-secondary/20 rounded-full" />
        <div className="absolute bottom-[25%] right-[40%] w-3 h-3 bg-primary/15 rotate-45" />
        <div className="absolute top-[40%] left-[35%] w-5 h-5 border-2 border-secondary/15" />
        <div className="absolute bottom-[50%] right-[15%] w-4 h-4 bg-secondary/10 rounded-full" />
      </div>

      {/* Subtle radial gradient overlay for depth */}
      <div className="absolute inset-0 bg-radial-gradient from-transparent via-transparent to-background/50" 
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, transparent 0%, transparent 50%, hsl(var(--background) / 0.3) 100%)'
        }}
      />
    </div>
  );
};
